import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Analytics from '../services/analytics';
import TitleHelmet from 'components/TitleHelmet';
import Syringe from '../components/SyringeSVG';
import 'css/tooltips.css';
import { CalculatorInput, CalculatorWarning } from 'components/calc';
import { useCalculatorStorage } from 'components/hooks/useCalculatorStorage';
import { useQueryParams } from 'components/hooks/useQueryParams';
import OtherCalcs from 'components/OtherCalcs';

const ZepboundReverseCalc: React.FC = () => {
  const navigate = useNavigate();
  const { zepboundListedDosage: queryListedDosage, units: queryUnits } = useQueryParams();

  // State for listed Zepbound dosage on the vial
  const [listedDosage, setListedDosage] = useCalculatorStorage({
    key: 'zepboundListedDosage',
    initialValue: '2.5',
    queryValue: queryListedDosage || undefined,
  });

  const [units, setUnits] = useCalculatorStorage({
    key: 'units',
    initialValue: '',
    queryValue: queryUnits || undefined,
  });

  const [calculatedDosage, setCalculatedDosage] = useState<number | null>(null);
  const [showInputs, setShowInputs] = useState<boolean>(true);

  const handleCalculate = (): void => {
    const listedDosageValue = parseFloat(listedDosage);
    // Zepbound concentration is always 2 * listedDosage (since 0.5mL volume for listed dose)
    const concentrationValue = 2 * listedDosageValue;
    const unitsValue = parseFloat(units);

    if (listedDosageValue > 0 && unitsValue > 0) {
      const dosage = (unitsValue / 100) * concentrationValue;
      setCalculatedDosage(dosage);
      setShowInputs(false);

      // Scroll to top when calculation is submitted for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Update query params for listedDosage (z) and units (u)
      navigate(`?z=${listedDosage}&u=${units}`, { replace: true });

      Analytics.trackCalculation('ZepboundReverse', {
        listedDosage: listedDosageValue,
        units: unitsValue,
        calculatedConcentration: concentrationValue,
        result: dosage,
      });
    } else {
      alert('Please select a listed dosage and enter a valid value for units.');
    }
  };

  const handleShowInputs = (): void => {
    setShowInputs(true);
    setCalculatedDosage(null);
  };

  const zepboundDoses = [2.5, 5, 7.5, 10];

  // Helper function to get the color class based on dose
  const getDoseButtonColorClass = (doseValue: number): string => {
    switch (doseValue) {
      case 2.5:
        return 'zep-dose-button-grey';
      case 5:
        return 'zep-dose-button-purple';
      case 7.5:
        return 'zep-dose-button-teal';
      case 10:
        return 'zep-dose-button-pink';
      default:
        return ''; // Should not happen with current zepboundDoses
    }
  };

  return (
    <div className="App">
      <TitleHelmet
        title="Fat Scientist | Zepbound Reverse Dose Calculator"
        description="Zepbound dosage verification calculator. Determine the exact mg of Zepbound you took based on your vial's listed dosage and units injected."
      />
      <header className="App-header">
        <Link to="/dosage-calculator" className="smaller-text">
          Looking for the standard Dose Calculator?
        </Link>
        <h1>Zepbound - How Much Did I Take?</h1>
        <p>
          For Zepbound vial users only: Select the dosage listed on your vial and enter the number of units you injected
          to calculate the actual dosage in milligrams (mg) you administered.
        </p>
      </header>
      {showInputs ? (
        <div className="calculator-container">
          <div className="zep-input-container">
            <div className="zep-input-column zep-input-column-left">
              <label
                htmlFor="listedDosageLabel"
                className="calculator-label"
                style={{ marginBottom: '0.5rem', display: 'block' }}
              >
                Listed Dosage on Vial (mg):
              </label>
              <div
                id="listedDosageLabel"
                className="zep-dosage-options-container"
                role="radiogroup"
                aria-labelledby="listedDosageLabel"
              >
                {zepboundDoses.map(dose => (
                  <button
                    key={dose}
                    type="button"
                    role="radio"
                    aria-checked={listedDosage === dose.toString()}
                    className={`zep-dosage-option-button ${
                      listedDosage === dose.toString() ? 'selected' : ''
                    } ${getDoseButtonColorClass(dose)}`}
                    onClick={() => setListedDosage(dose.toString())}
                  >
                    {dose} mg
                  </button>
                ))}
              </div>
            </div>

            <div className="zep-input-column zep-input-column-right">
              <div style={{ marginTop: '0px' }}>
                <CalculatorInput
                  id="units"
                  value={units}
                  onChange={setUnits}
                  unit="units"
                  placeholder="e.g., 25"
                  label="Units Injected"
                />
              </div>
            </div>
          </div>
          <button onClick={handleCalculate} className="calculate-button">
            Calculate
          </button>
        </div>
      ) : (
        <span>
          &#9664;{' '}
          <Link className="smaller-text" onClick={handleShowInputs} to="">
            Reset calculator and start again
          </Link>
        </span>
      )}
      {calculatedDosage !== null && (
        <div className="result centered">
          <div>
            For {units} units from a vial labeled {listedDosage} mg,
            <div className="resultsText">
              you took {Number(calculatedDosage.toFixed(2)).toString()} mg of medication.
              <br />
            </div>
            <Syringe calculatedVolume={parseFloat(units) / 100} />
            {calculatedDosage >= 20 ? (
              <CalculatorWarning
                type="urgent"
                message={
                  <>
                    The calculated dose of {Number(calculatedDosage.toFixed(2)).toString()}mg is very high! Contact your
                    healthcare provider IMMEDIATELY.
                  </>
                }
              />
            ) : calculatedDosage > parseFloat(listedDosage) && calculatedDosage < 20 ? (
              <CalculatorWarning
                message={`You took ${Number(calculatedDosage.toFixed(2)).toString()}mg, which is more than the ${listedDosage}mg dose indicated on your vial. If this was unintentional, please monitor for side effects and consult your healthcare provider.`}
              />
            ) : (
              <CalculatorWarning message="If this dose is different from what your healthcare provider prescribed, or if you have any concerns, please contact them." />
            )}
          </div>
        </div>
      )}
      <p className="disclaimer">
        Disclaimer: This calculator is for informational purposes only and is not a substitute for professional medical
        advice. Always verify your dose with your healthcare provider. Fat Scientist is not responsible for any
        consequences from using this tool.
      </p>

      <OtherCalcs />
    </div>
  );
};

export default ZepboundReverseCalc;
